import { useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import {
  AutoAwesome,
  CheckCircleOutline,
  ErrorOutline,
  InfoOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import http from "../../../utils/http";
import { extractErrorMessage } from "../../../utils/toast";
import type { CohortCriteriaDSL } from "./types";

type UnmappedReason =
  | "no_matching_field"
  | "study_level"
  | "identity"
  | "unsupported"
  | "invalid_value";

export interface AiInterpretResult {
  dsl: CohortCriteriaDSL;
  questions: any[];
  summary: string[];
  conditionCount: number;
  unmapped: { text: string; reason: UnmappedReason; detail?: string }[];
  interpretedBy: string;
}

interface AiSearchCardProps {
  existingCriteriaCount: number;
  canUndo: boolean;
  onApply: (result: AiInterpretResult, mode: "replace" | "add") => void;
  onUndo: () => void;
}

type Status =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "choose"; result: AiInterpretResult }
  | { kind: "applied"; result: AiInterpretResult }
  | { kind: "empty"; result: AiInterpretResult }
  | { kind: "error"; unavailable: boolean; message: string };

const SUGGESTIONS = [
  "Women over 50 in Texas",
  "Spanish speakers not in a study in the last 6 months",
  "Current or former smokers over 40",
  "Everyone except people tagged do not contact",
];

const REASON_TEXT: Record<UnmappedReason, string> = {
  no_matching_field: "No registry field matches this.",
  study_level:
    "Study-level criteria aren't part of registry search. Use the study's Cohort Builder.",
  identity:
    "Names, emails and phone numbers can't be searched with AI. Use All Participants search.",
  unsupported: "This can't be expressed as a registry filter.",
  invalid_value: "That value isn't one of the field's options.",
};

const purple = "#872EE5";

const Notice = ({
  tone,
  children,
}: {
  tone: "warn" | "error" | "info";
  children: React.ReactNode;
}) => {
  const palette = {
    warn: { bg: "#FFF8EB", border: "#F6D9A8", fg: "#7C2D12", icon: "#B45309" },
    error: { bg: "#FEF2F2", border: "#FBCACA", fg: "#7F1D1D", icon: "#B91C1C" },
    info: { bg: "#F3F4F6", border: "#E0E3EB", fg: "#374151", icon: "#374151" },
  }[tone];
  const Icon =
    tone === "warn"
      ? WarningAmberOutlined
      : tone === "error"
        ? ErrorOutline
        : InfoOutlined;
  return (
    <Box
      role="note"
      sx={{
        display: "flex",
        gap: 1.25,
        alignItems: "flex-start",
        bgcolor: palette.bg,
        border: `1px solid ${palette.border}`,
        borderRadius: "8px",
        px: 1.5,
        py: 1.25,
      }}
    >
      <Icon sx={{ fontSize: 18, color: palette.icon, mt: "1px" }} />
      <Box sx={{ fontSize: 13, lineHeight: 1.5, color: palette.fg }}>
        {children}
      </Box>
    </Box>
  );
};

function UnmappedList({ items }: { items: AiInterpretResult["unmapped"] }) {
  if (!items.length) return null;
  return (
    <Notice tone="warn">
      <b>
        {items.length === 1
          ? "Not applied:"
          : `${items.length} phrases not applied:`}
      </b>
      <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.25 }}>
        {items.map((item, i) => (
          <li key={`${item.text}-${i}`}>
            “{item.text}” — {item.detail ?? REASON_TEXT[item.reason]}
          </li>
        ))}
      </Box>
      <Box sx={{ mt: 0.5 }}>
        Results won't filter on these. Add a criterion manually if a matching
        field exists.
      </Box>
    </Notice>
  );
}

export function AiSearchCard({
  existingCriteriaCount,
  canUndo,
  onApply,
  onUndo,
}: AiSearchCardProps) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const abortRef = useRef<AbortController | null>(null);

  const trimmed = query.trim();
  const loading = status.kind === "loading";

  const apply = (result: AiInterpretResult, mode: "replace" | "add") => {
    onApply(result, mode);
    setStatus({ kind: "applied", result });
  };

  const submit = async (text = trimmed) => {
    if (text.length < 3 || loading) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus({ kind: "loading" });
    try {
      const res = await http.post(
        "/admin/registry/cohort-query/interpret",
        { query: text },
        { signal: controller.signal },
      );
      const result: AiInterpretResult = res.data.data;
      if (result.conditionCount === 0) {
        setStatus({ kind: "empty", result });
      } else if (existingCriteriaCount > 0) {
        setStatus({ kind: "choose", result });
      } else {
        apply(result, "replace");
      }
    } catch (error: any) {
      if (controller.signal.aborted) return;
      setStatus({
        kind: "error",
        unavailable: error?.response?.status === 503,
        message: extractErrorMessage(error),
      });
    }
  };

  const cancel = () => {
    abortRef.current?.abort();
    setStatus({ kind: "idle" });
  };

  return (
    <Box
      component="section"
      aria-labelledby="ai-search-title"
      sx={{
        bgcolor: "background.paper",
        border: "1px solid #E4D2FB",
        borderRadius: "12px",
        boxShadow: "0px 1px 3px rgba(0,0,0,0.25)",
        p: 2.5,
        mb: 3,
        display: "flex",
        flexDirection: "column",
        gap: 1.75,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <AutoAwesome sx={{ color: purple, fontSize: 20 }} />
        <Typography id="ai-search-title" variant="h3" sx={{ fontSize: 16 }}>
          Describe who you're looking for
        </Typography>
        <Chip
          label="Registry-wide"
          size="small"
          variant="outlined"
          sx={{ ml: "auto", color: "text.secondary" }}
        />
      </Box>

      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}
      >
        <TextField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          multiline
          minRows={2}
          maxRows={4}
          disabled={loading}
          placeholder="e.g. Women over 50 in Texas or Florida with type 2 diabetes who haven't been in a study in the last 6 months"
          inputProps={{ maxLength: 500, "aria-label": "Search description" }}
          sx={{ flex: "1 1 520px", minWidth: 0 }}
        />
        <Button
          type="submit"
          variant={status.kind === "applied" ? "outlined" : "contained"}
          disabled={trimmed.length < 3 || loading}
          startIcon={
            loading ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <AutoAwesome />
            )
          }
          sx={{ minHeight: 48, px: 2.5, alignSelf: "stretch" }}
        >
          {status.kind === "applied" ? "Rebuild" : "Build criteria"}
        </Button>
      </Box>

      {status.kind === "idle" && (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontWeight: 500 }}
          >
            Try:
          </Typography>
          {SUGGESTIONS.map((s) => (
            <Chip
              key={s}
              label={s}
              clickable
              onClick={() => {
                setQuery(s);
                submit(s);
              }}
              sx={{
                bgcolor: "#F5EEFE",
                color: "#5B21B6",
                border: "1px solid #E4D2FB",
                fontWeight: 500,
              }}
            />
          ))}
        </Box>
      )}

      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ color: "#6D28D9", fontWeight: 500, fontSize: 14 }}>
            Building criteria… usually a few seconds
          </Typography>
          <Button size="small" onClick={cancel} sx={{ ml: "auto" }}>
            Cancel
          </Button>
        </Box>
      )}

      {status.kind === "choose" && (
        <>
          <Notice tone="info">
            Found <b>{status.result.conditionCount}</b>{" "}
            {status.result.conditionCount === 1 ? "condition" : "conditions"}.
            The builder already has <b>{existingCriteriaCount}</b>{" "}
            {existingCriteriaCount === 1 ? "criterion" : "criteria"}. What
            should happen to them?
          </Notice>
          <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              onClick={() => apply(status.result, "add")}
            >
              Add to current criteria
            </Button>
            <Button
              variant="outlined"
              onClick={() => apply(status.result, "replace")}
            >
              Replace all
            </Button>
            <Button onClick={() => setStatus({ kind: "idle" })}>Cancel</Button>
          </Box>
        </>
      )}

      {status.kind === "applied" && (
        <Box
          sx={{
            bgcolor: "#FAF7FF",
            border: "1px solid #EDE3FC",
            borderRadius: "10px",
            px: 2,
            py: 1.75,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              flexWrap: "wrap",
            }}
          >
            <CheckCircleOutline sx={{ color: purple, fontSize: 18 }} />
            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
              {status.result.conditionCount}{" "}
              {status.result.conditionCount === 1 ? "condition" : "conditions"}{" "}
              added to the builder
            </Typography>
            <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
              Review them, then click Run.
            </Typography>
            {canUndo && (
              <Button
                size="small"
                onClick={() => {
                  onUndo();
                  setStatus({ kind: "idle" });
                }}
                sx={{ ml: "auto" }}
              >
                Undo
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {status.result.summary.map((line, i) => (
              <Chip
                key={`${line}-${i}`}
                label={line}
                variant="outlined"
                sx={{ bgcolor: "background.paper", borderRadius: "8px" }}
              />
            ))}
          </Box>
          <UnmappedList items={status.result.unmapped} />
        </Box>
      )}

      {status.kind === "empty" && (
        <>
          <Notice tone="error">
            <b>Couldn't turn this into criteria.</b> The builder wasn't changed,
            and nothing was run.
          </Notice>
          <UnmappedList items={status.result.unmapped} />
        </>
      )}

      {status.kind === "error" && (
        <Notice tone={status.unavailable ? "info" : "error"}>
          <b>{status.message}</b>
          {status.unavailable && (
            <Box sx={{ mt: 0.75 }}>
              <Button size="small" variant="outlined" onClick={() => submit()}>
                Try again
              </Button>
            </Box>
          )}
        </Notice>
      )}

      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "flex-start",
          borderTop: "1px solid #F0F1F4",
          pt: 1.5,
          color: "text.secondary",
          fontSize: 12,
          lineHeight: 1.5,
        }}
      >
        <InfoOutlined sx={{ fontSize: 14, mt: "2px" }} />
        <span>
          AI turns your sentence into the criteria below. Nothing runs until you
          click <b>Run</b>. Don't include names, emails or phone numbers; your
          text is sent to the AI provider, and identity fields aren't searchable
          here.
        </span>
      </Box>
    </Box>
  );
}
